import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import { API, errorText } from '@/utils/api';

// What each region, store or customer was dispatched in the period, one row
// each: totals, realised ₹/m, fabric split (by ₹ or metres — the page's
// switch), realised ₹/m inside Shirting and Suiting, and its biggest
// qualities. A row opens that region / account across the whole tab.

const BY = [
  { key: 'region', label: 'Region-wise' },
  { key: 'store', label: 'Store-wise' },
  { key: 'customer', label: 'Customer-wise' },
];
const FABRICS = [
  { key: 'Shirting', color: '#0EA5E9' },
  { key: 'Suiting', color: '#7C3AED' },
  { key: 'Ethnic', color: '#EC4899' },
  { key: 'Other', color: '#64748B' },
];

const inr = (v) => {
  const a = Math.abs(v || 0);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${Math.round(v || 0).toLocaleString('en-IN')}`;
};
const num = (v) => Math.round(v || 0).toLocaleString('en-IN');
const rate = (v) => (v == null ? '—' : `₹${Math.round(v).toLocaleString('en-IN')}`);

// "Other" on screen = Other + Unclear (both are outside the head fabrics).
const fabricShare = (row, fab, key) => {
  const f = row.fabrics || {};
  if (fab === 'Other') return (f.Other?.[key] || 0) + (f.Unclear?.[key] || 0);
  return f[fab]?.[key] || 0;
};

function MixBar({ row, shareKey }) {
  return (
    <div className="flex h-2.5 w-28 rounded-full overflow-hidden bg-gray-100">
      {FABRICS.map(f => {
        const v = fabricShare(row, f.key, shareKey);
        return v > 0 ? <div key={f.key} style={{ width: `${v}%`, background: f.color }} title={`${f.key} ${v.toFixed(1)}%`} /> : null;
      })}
    </div>
  );
}

export default function ProductMixBreakdown({ selectedDivision, periodType, periodValue, periodLabel, metric, account, onOpenRegion, onOpenAccount }) {
  const [by, setBy] = useState('region');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'value', dir: -1 });

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(`${API}/analytics/region-product-mix/breakdown`, {
      params: {
        by,
        division: selectedDivision !== 'all' ? selectedDivision : undefined,
        period_type: periodType, period_value: periodValue,
        ...(account ? { scope_kind: account.kind, scope: account.key } : {}),
      },
      withCredentials: true,
    }).then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) { toast.error(errorText(e) || 'Could not load the breakdown'); setData(null); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [by, selectedDivision, periodType, periodValue, account?.kind, account?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setRegionFilter('all'); setQ(''); }, [by]);

  const shareKey = metric === 'value' ? 'pct_value' : 'pct_qty';
  const regionsInRows = useMemo(() => [...new Set((data?.rows || []).map(r => r.region).filter(Boolean))].sort(), [data]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = (data?.rows || []).filter(r =>
      (!s || `${r.label} ${r.sub} ${r.region}`.toLowerCase().includes(s))
      && (regionFilter === 'all' || r.region === regionFilter));
    const val = (r) => {
      if (sort.key.startsWith('share:')) return fabricShare(r, sort.key.slice(6), shareKey);
      if (sort.key.startsWith('rate:')) return r.fabrics?.[sort.key.slice(5)]?.rate ?? -1;
      if (sort.key === 'label') return r.label.toLowerCase();
      return r[sort.key] ?? -1;
    };
    list = [...list].sort((a, b) => {
      const x = val(a); const y = val(b);
      return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
    });
    return list;
  }, [data, q, regionFilter, sort, shareKey]);

  const Th = ({ k, children, right = true, title }) => (
    <th title={title}
      className={`px-2.5 py-2 font-medium cursor-pointer select-none whitespace-nowrap hover:text-gray-900 ${right ? 'text-right' : 'text-left'}`}
      onClick={() => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : (k === 'label' ? 1 : -1) }))}>
      {children}
      {sort.key === k && (sort.dir < 0 ? <ArrowDown size={10} className="inline ml-0.5" /> : <ArrowUp size={10} className="inline ml-0.5" />)}
    </th>
  );

  const total = data?.total;
  const unit = metric === 'value' ? '₹' : 'metres';
  const canOpen = (r) => (by === 'region' ? !account : !!r.account);

  return (
    <div className="space-y-3">
      <Card className="border border-gray-200">
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {BY.map(b => (
              <button key={b.key} type="button" onClick={() => setBy(b.key)}
                className={`text-xs px-3 py-1 rounded-md transition font-medium ${by === b.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
                {b.label}
              </button>
            ))}
          </div>
          {by !== 'region' && regionsInRows.length > 1 && (
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="h-8 text-xs w-[160px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {regionsInRows.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${by === 'region' ? 'regions' : by === 'store' ? 'stores' : 'customers'}…`}
            className="h-8 text-xs w-[220px]" />
          <span className="text-xs text-gray-500 ml-auto">
            {periodLabel}{account ? ` · within ${account.label}` : ''} · shares by {unit} · click a column to sort, a row to open it
          </span>
        </CardContent>
      </Card>

      <Card className="border border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500 text-sm gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : !rows.length ? (
            <p className="py-10 text-center text-sm text-gray-500">
              {by === 'store' ? 'No store-wise dispatches in this period — stores are recorded for TAS accounts.' : 'Nothing dispatched in this period.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                  <tr>
                    <Th k="label" right={false}>{by === 'region' ? 'Region' : by === 'store' ? 'Store' : 'Customer'}</Th>
                    {by !== 'region' && <th className="px-2.5 py-2 text-left font-medium">Region</th>}
                    <Th k="value">Dispatched</Th>
                    <Th k="metres">Metres</Th>
                    <Th k="rate" title="Realised ₹ per metre — net value ÷ metres, after discounts">Avg ₹/m</Th>
                    <th className="px-2.5 py-2 text-left font-medium">Mix ({unit})</th>
                    {FABRICS.map(f => (
                      <Th key={f.key} k={`share:${f.key}`} title={`${f.key} share of the ${unit} dispatched${f.key === 'Other' ? ' (incl. unclassified)' : ''}`}>
                        <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: f.color }} />{f.key} %
                      </Th>
                    ))}
                    <Th k="rate:Shirting" title="Realised ₹/m inside Shirting">Shirting ₹/m</Th>
                    <Th k="rate:Suiting" title="Realised ₹/m inside Suiting">Suiting ₹/m</Th>
                    <th className="px-2.5 py-2 text-left font-medium">Biggest qualities</th>
                  </tr>
                </thead>
                <tbody>
                  {total && (
                    <tr className="bg-indigo-50/50 font-semibold border-b border-gray-200">
                      <td className="px-2.5 py-2 text-gray-900">All {by === 'region' ? 'regions' : by === 'store' ? 'stores' : 'customers'}{account ? ` · ${account.label}` : ''}</td>
                      {by !== 'region' && <td />}
                      <td className="px-2.5 py-2 text-right tabular-nums">{inr(total.value)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{num(total.metres)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{rate(total.rate)}</td>
                      <td className="px-2.5 py-2"><MixBar row={total} shareKey={shareKey} /></td>
                      {FABRICS.map(f => <td key={f.key} className="px-2.5 py-2 text-right tabular-nums">{fabricShare(total, f.key, shareKey).toFixed(1)}%</td>)}
                      <td className="px-2.5 py-2 text-right tabular-nums">{rate(total.fabrics?.Shirting?.rate)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{rate(total.fabrics?.Suiting?.rate)}</td>
                      <td className="px-2.5 py-2 text-gray-600 font-normal truncate max-w-[260px]">{(total.top || []).map(t => t.name).join(', ')}</td>
                    </tr>
                  )}
                  {rows.map(r => (
                    <tr key={r.key}
                      onClick={() => {
                        if (!canOpen(r)) return;
                        if (by === 'region') onOpenRegion(r.label);
                        else onOpenAccount(r.account, r.label);
                      }}
                      className={`border-t border-gray-100 ${canOpen(r) ? 'cursor-pointer hover:bg-indigo-50/40' : ''}`}
                      title={canOpen(r) ? `Open ${r.label} across the whole Product Mix tab` : undefined}>
                      <td className="px-2.5 py-1.5">
                        <div className="font-medium text-gray-900">{r.label}</div>
                        {r.sub && <div className="text-[10px] text-gray-500">{r.sub}</div>}
                      </td>
                      {by !== 'region' && <td className="px-2.5 py-1.5 text-gray-600 whitespace-nowrap">{r.region || '—'}</td>}
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{inr(r.value)}</td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{num(r.metres)}</td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{rate(r.rate)}</td>
                      <td className="px-2.5 py-1.5"><MixBar row={r} shareKey={shareKey} /></td>
                      {FABRICS.map(f => {
                        const v = fabricShare(r, f.key, shareKey);
                        return <td key={f.key} className={`px-2.5 py-1.5 text-right tabular-nums ${v ? 'text-gray-800' : 'text-gray-300'}`}>{v ? `${v.toFixed(1)}%` : '—'}</td>;
                      })}
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{rate(r.fabrics?.Shirting?.rate)}</td>
                      <td className="px-2.5 py-1.5 text-right tabular-nums">{rate(r.fabrics?.Suiting?.rate)}</td>
                      <td className="px-2.5 py-1.5 text-gray-600">
                        <div className="truncate max-w-[260px]">
                          {(r.top || []).map(t => `${t.name} ${(t[shareKey] || 0).toFixed(0)}%`).join(' · ')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
